import { memo } from 'react';

import { Card, CardMedia, CardActions, CardContent, Chip, Grid, Typography, Rating, SxProps, Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useBrowserStorage } from '@aspendental/shared-utils/react';

import { GoogleMapTemplate, BffWorkingHours, Button, Maybe, NamedAttribute, Facility } from '@/types/generated';
import { resolvePhoneNumberFormat } from '@/utils';
import { FACILITY_CODE_STORAGE_KEY, metaData, sys } from '@/constants';
import { useAppContext, useFacilityContext } from '@/context';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import ContentfulButton from '@/components/ContentfulButton';
import { SchedulingSystemType } from '@/types';

import TAGLink from '../../../TAGLink';
import TAGButton from '../../../TAGButton';
import { getOfficeDetailsUrl, getScheduleButtonUrl } from '../../GoogleMap.helpers';

import {
	getCardTextContainerStyles,
	getLocationCardStyles,
	getLocationCardImageStyles,
	getGridDisplayStyles,
	cardActionStyles,
	getCardContentStyles,
	getTitleAndDistanceStyles,
	locationCardDistanceContainerStyles,
	locationCardTitleContainerStyles,
	directionsLinkStyles,
	locationCardsInHeaderStyles,
	setAsOfficeButtonStyles,
} from './LocationCard.styles';
import LocationStatus from './LocationStatus';

export interface INearbyFacility extends Facility {
	attributes?: Maybe<NamedAttribute>[];
}

export interface ILocationCardProps {
	location: INearbyFacility;
	isSmallScreen?: boolean;
	isFromGoogleMaps?: boolean;
	distance?: number;
	index?: number;
	locationScheduleButtonUrl?: GoogleMapTemplate['locationScheduleButtonUrl'];
	googleMapsBusinessQuery?: GoogleMapTemplate['googleMapsBusinessQuery'];
	defaultImage?: GoogleMapTemplate['defaultImage'];
	selectedImage?: GoogleMapTemplate['selectedImage'];
	locationRating?: boolean;
	locationScheduling?: { schedulingSystem?: SchedulingSystemType; accountId?: string };
	facilityBasePath?: string;
	isCurrentSelectedFacility?: boolean;
	holidayHoursMessageText?: string;
	displayAsScrollableInOneRow?: boolean;
	hideCardImage?: boolean;
	revspringAccountId?: string | null;
	isHorizontalScrolling?: boolean;
	facilityBadgeLabel?: string | null;
	facilityButtonLabel?: string | null;
	scheduleButton?: Maybe<Button>;
	officeDetailsButton?: Maybe<Button>;
	isLocationCardsInHeader?: boolean;
	setAsOfficeButtonText?: Maybe<string>;
	showSetOfficeButtonInGoogleMapsCards?: boolean;
}

interface IsCurrentSelectedFacility {
	index: number | undefined;
	isFromGoogleMaps: boolean;
	isCurrentSelectedFacility: boolean | undefined;
}

export function displayAsCurrentSelectedFacility({
	index,
	isFromGoogleMaps,
	isCurrentSelectedFacility,
}: IsCurrentSelectedFacility) {
	return (index === 0 && !isFromGoogleMaps) || isCurrentSelectedFacility;
}

const SetAsOfficeButton = (props: {
	showSetOfficeButtonInGoogleMapsCards: boolean;
	setAsOfficeButtonText: string;
	index: number;
	facilityCode: string;
	useSessionStorageFacilityCode: boolean;
}) => {
	const {
		showSetOfficeButtonInGoogleMapsCards,
		setAsOfficeButtonText,
		index,
		facilityCode,
		useSessionStorageFacilityCode = false,
	} = props;
	const { setValue } = useBrowserStorage({
		key: FACILITY_CODE_STORAGE_KEY,
		storage: useSessionStorageFacilityCode ? globalThis.sessionStorage : globalThis.localStorage,
	});
	return (
		<>
			{showSetOfficeButtonInGoogleMapsCards && (
				<TAGButton
					dataTestId={`set_office_button_${index}`}
					variant="tertiaryDefault"
					size="small"
					sx={setAsOfficeButtonStyles}
					onClick={() => {
						setValue(facilityCode);
					}}
				>
					{setAsOfficeButtonText ?? 'Set as office'}
				</TAGButton>
			)}
		</>
	);
};

export default memo(function LocationCard(props: ILocationCardProps) {
	const { config } = useAppContext();
	const { facilityBrand } = useFacilityContext();
	const schedulingPath = config.features.otherApps.schedulingPath;
	const {
		location,
		isSmallScreen,
		isFromGoogleMaps = false,
		distance,
		index,
		defaultImage,
		selectedImage,
		googleMapsBusinessQuery,
		locationRating = false,
		locationScheduling,
		facilityBasePath,
		isCurrentSelectedFacility,
		holidayHoursMessageText,
		// used in nearbylocations template
		displayAsScrollableInOneRow = false,
		hideCardImage = false,
		isHorizontalScrolling = false,
		facilityBadgeLabel,
		facilityButtonLabel,
		scheduleButton,
		officeDetailsButton,
		isLocationCardsInHeader,
		setAsOfficeButtonText,
		showSetOfficeButtonInGoogleMapsCards,
	} = props;

	const { stateCode: state, city, address1: address, zipCode } = location.address;

	const officeDetailsUrl = getOfficeDetailsUrl({
		facilityBasePath,
		themeId: config.themeId,
		address: location.address,
	});

	const locationPhoneNumber = location?.technicalInfrastructure?.phoneNumber ?? location?.phoneNumber;
	const isOnPageScheduling =
		locationScheduling?.schedulingSystem === ('Revspring' as SchedulingSystemType) ||
		locationScheduling?.schedulingSystem === ('Clockwise' as SchedulingSystemType);
	const useSessionStorageFacilityCode = config.featureFlags?.useSessionStorageFacilityCode;
	const scheduleType = location?.attributes
		?.find((attribute) => attribute?.name === 'Schedule Type')
		?.value?.toLowerCase();

	return (
		<Card
			data-test-id={`location_card_${index as number}`}
			sx={
				getLocationCardStyles(
					isSmallScreen,
					displayAsScrollableInOneRow,
					displayAsCurrentSelectedFacility({ index, isFromGoogleMaps, isCurrentSelectedFacility }),
					isHorizontalScrolling
				) as SxProps
			}
		>
			<Grid
				container
				direction={isSmallScreen ? 'column' : 'row'}
				sx={getGridDisplayStyles(isSmallScreen)}
				spacing={0}
			>
				{!hideCardImage && (
					<Grid item xs={4}>
						<CardMedia
							data-test-id="section_card_media"
							component="img"
							alt=""
							title={
								displayAsCurrentSelectedFacility({
									index,
									isFromGoogleMaps,
									isCurrentSelectedFacility,
								})
									? selectedImage?.description || ''
									: defaultImage?.description || ''
							}
							image={
								displayAsCurrentSelectedFacility({
									index,
									isFromGoogleMaps,
									isCurrentSelectedFacility,
								})
									? selectedImage?.url || ''
									: defaultImage?.url || ''
							}
							sx={getLocationCardImageStyles(isSmallScreen)}
						/>
					</Grid>
				)}
				<Grid
					data-test-id="section_card_content"
					item
					// if image is not displayed, then use full 12 columns
					xs={hideCardImage ? 12 : 8}
					container
					spacing={2}
					sx={getCardTextContainerStyles(isSmallScreen)}
					gap={2}
				>
					<CardContent sx={getCardContentStyles(isSmallScreen)}>
						{/* Badge */}
						{displayAsCurrentSelectedFacility({
							index,
							isFromGoogleMaps,
							isCurrentSelectedFacility,
						}) && (
							<Grid item sx={{ pb: '0.125rem' }}>
								<Chip
									data-test-id="section_chip"
									label={facilityBadgeLabel || 'Current Office'}
									variant="lightSuccess"
									onDelete={undefined}
								/>
							</Grid>
						)}
						{/* title & distance */}
						<Grid
							data-test-id="section_title_distance"
							item
							container
							xs={12}
							sx={getTitleAndDistanceStyles(
								index,
								isSmallScreen,
								isFromGoogleMaps,
								isCurrentSelectedFacility
							)}
						>
							{/* Location */}
							<Grid item xs={9.5} sx={locationCardTitleContainerStyles}>
								<Typography data-test-id="section_card_title" variant="bodyMediumSemiBold">
									{location.name}
								</Typography>
							</Grid>

							{/* Distance */}
							{(index !== 0 || isFromGoogleMaps) && (
								<Grid xs={2.5} item sx={locationCardDistanceContainerStyles}>
									<Typography
										data-test-id="section_distance"
										color="text.tertiary"
										variant="bodySmallBook"
										sx={locationCardsInHeaderStyles(isLocationCardsInHeader)}
									>
										{distance && `${distance.toFixed(1)} mi`}
									</Typography>
								</Grid>
							)}
						</Grid>
						{/* rating, address & phone */}
						<Grid data-test-id="section_rating_address_phone" item xs={12}>
							<Grid container direction="column">
								{/* rating container */}
								{locationRating && ( //TO DO: connect to birdeye for reviews
									<Grid item xs={12} container justifyContent="flex-start" alignItems="center">
										<Grid item>
											<Rating
												data-test-id="section_rating"
												value={5}
												readOnly
												size="small"
												sx={{ color: 'warning.dark' }}
											/>
										</Grid>
									</Grid>
								)}
								{/* address */}
								<Grid item xs={12}>
									<Typography
										data-test-id="section_address"
										variant="bodySmallBook"
										color="text.secondary"
									>
										{address} {city}, {state} {zipCode}
									</Typography>
								</Grid>
								{/* directions button */}
								<Grid item xs={12}>
									<TAGLink
										dataTestId={`section_directions_${index as number}`}
										href={googleMapsBusinessQuery?.replace(
											/\[address\]/gi,
											`${address as string}, ${city as string}, ${state as string} ${
												zipCode as string
											}`
										)}
										openInNewTab
										schedulingDataLayer={{
											event: 'saa_flow',
											step_name: 'find_a_location',
											brand: 'WellNow',
											clinic_city: city,
											clinic_location: address,
											clinic_state: state,
										}}
									>
										<Box sx={directionsLinkStyles}>
											<Typography
												data-test-id="section_directions_text"
												variant="bodySmallBook"
												color="text.interactive"
												position="relative"
											>
												Get directions
												<Box sx={visuallyHidden}>
													{' '}
													to {config.name}, {location.name}
												</Box>
											</Typography>
											<TAGSvgIcon
												dataTestId="section_directions_icon"
												icon={'ExternalLink'}
												size={16}
												htmlColor="text.interactive"
												style={{ marginTop: '.125rem' }}
											/>
										</Box>
									</TAGLink>
								</Grid>
								{/* Phone */}
								<Grid item xs={12}>
									<TAGLink href={`tel:${locationPhoneNumber?.replace(/[- )(]/g, String) as string}`}>
										<Typography
											data-test-id="section_phone_number"
											variant="bodySmallBook"
											color="text.interactive"
										>
											{resolvePhoneNumberFormat(locationPhoneNumber as string)}
										</Typography>
									</TAGLink>
								</Grid>
								{isFromGoogleMaps && location.workingHours !== null && location.location?.timeZone && (
									<Grid item xs={12}>
										<LocationStatus
											bffWorkingHours={location.workingHours as BffWorkingHours[]}
											holidayHoursMessageText={holidayHoursMessageText || ''}
											timeZoneId={location.location?.timeZone || ''}
										/>
									</Grid>
								)}
							</Grid>
						</Grid>
					</CardContent>
					<CardActions data-test-id="section_card_actions" sx={cardActionStyles}>
						{scheduleButton ? (
							<>
								<ContentfulButton
									key={scheduleButton.sys.id}
									{...scheduleButton}
									dataTestId="schedule_button"
									buttonLink={getScheduleButtonUrl({
										schedulingPathFromConfig: schedulingPath,
										facilityBrand,
										location,
										locationScheduling,
									})}
									variant={isOnPageScheduling ? 'Primary Default' : scheduleButton.variant}
									defaultVariant={isOnPageScheduling ? 'primaryDefault' : 'secondaryDefault'}
									analyticsContext={{
										...scheduleButton.analyticsContext,
										event: scheduleButton.analyticsContext?.event || 'mapbox_search_selection',
										appointmentSelectedFacilityId: location.code,
										appointmentSelectedFacilityName: location.name,
										...(scheduleType && { scheduling: scheduleType }),
										contentfulMetadata: metaData,
										sys: sys,
										_id: '',
									}}
									ariaLabel={`${scheduleButton?.label ?? ''} at ${config.name}, ${location.name}`}
								/>
								{officeDetailsButton && (
									<ContentfulButton
										key={officeDetailsButton.sys.id}
										{...officeDetailsButton}
										dataTestId={`office_details_button_${index as number}`}
										buttonLink={officeDetailsUrl}
										defaultVariant="primaryHC"
										ariaLabel={`${config.name}, ${location.name} ${
											officeDetailsButton?.label ?? ''
										}`}
									/>
								)}
								{!isCurrentSelectedFacility && (
									<SetAsOfficeButton
										setAsOfficeButtonText={setAsOfficeButtonText ?? 'Set as office'}
										showSetOfficeButtonInGoogleMapsCards={
											showSetOfficeButtonInGoogleMapsCards || false
										}
										index={index as number}
										facilityCode={location?.code}
										useSessionStorageFacilityCode={useSessionStorageFacilityCode}
									/>
								)}
							</>
						) : (
							// TODO: remove support for these deprecated buttons and make use of the Contentful driven buttons above
							<>
								<TAGButton
									dataTestId="schedule_button_not_contentful"
									href={getScheduleButtonUrl({
										schedulingPathFromConfig: schedulingPath,
										facilityBrand,
										location,
										locationScheduling,
									})}
									analyticsContext={{
										event: 'mapbox_search_selection',
										appointmentSelectedFacilityId: location?.code,
										appointmentSelectedFacilityName: location?.name,
										...(scheduleType && { scheduling: scheduleType }),
										contentfulMetadata: metaData,
										sys: sys,
										_id: '',
									}}
									variant={isOnPageScheduling ? 'primaryDefault' : 'secondaryDefault'}
									size="XS"
									aria-label={`Schedule at ${config.name}, ${location.name}`}
								>
									Schedule
								</TAGButton>
								<TAGButton
									dataTestId={`office_details_button_${index as number}`}
									variant="primaryHC"
									size="XS"
									href={officeDetailsUrl}
									sx={{ padding: '0.375rem' }}
									aria-label={`${config.name}, ${location.name} ${
										facilityButtonLabel ?? 'Office Details'
									}`}
								>
									{facilityButtonLabel || 'Office Details'}
								</TAGButton>
								{!isCurrentSelectedFacility && (
									<SetAsOfficeButton
										setAsOfficeButtonText={setAsOfficeButtonText ?? 'Set as office'}
										showSetOfficeButtonInGoogleMapsCards={
											showSetOfficeButtonInGoogleMapsCards || false
										}
										index={index as number}
										facilityCode={location?.code}
										useSessionStorageFacilityCode={useSessionStorageFacilityCode}
									/>
								)}
							</>
						)}
					</CardActions>
				</Grid>
			</Grid>
		</Card>
	);
});
